<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-back-button default-href="/"></ion-back-button>
                </ion-buttons>
                <ion-title></ion-title>
            </ion-toolbar>
        </ion-header>

        <ion-content>
            <div class="manager-layout">
                <!-- Group sidebar -->
                <div class="group-sidebar">
                    <div v-if="loadingGroups" class="sidebar-loading">
                        <ion-spinner name="crescent"></ion-spinner>
                    </div>
                    <div v-else-if="loadingError" class="sidebar-error">{{ loadingError }}</div>
                    <div v-else class="group-list">
                        <button
                            v-for="group of groups"
                            :key="group.id"
                            class="group-item"
                            :class="{ 'group-item--active': selectedView === 'group' && selectedGroup && selectedGroup.id === group.id }"
                            @click="selectGroup(group)">
                            <span class="group-name">{{ group.name }}</span>
                            <span v-if="groupFileCount(group)" class="group-count-pill">
                                {{ groupFileCount(group) }}
                            </span>
                        </button>
                        <button
                            class="group-item"
                            :class="{ 'group-item--active': selectedView === 'resources' }"
                            @click="selectResources">
                            <span class="group-name">Resources</span>
                            <span v-if="resources.length" class="group-count-pill">
                                {{ resources.length }}
                            </span>
                        </button>
                    </div>
                </div>

                <!-- File panel -->
                <div class="file-panel">
                    <div v-if="!selectedView" class="file-panel-empty">
                        Select a group to view files.
                    </div>

                    <!-- Resources panel -->
                    <template v-else-if="selectedView === 'resources'">
                        <div class="file-panel-header">
                            <h2 class="group-title">Resources</h2>
                            <div class="panel-controls">
                                <input
                                    v-if="resources.length > 0"
                                    v-model="searchQuery"
                                    type="search"
                                    class="search-input"
                                    placeholder="Search resources…" />
                                <button type="button" class="upload-label header-upload-btn" @click="openResourceUploadModal">
                                    <ion-icon :icon="cloudUploadOutline"></ion-icon>
                                    <span>Add Resource</span>
                                </button>
                            </div>
                        </div>
                        <div v-if="loadingResources" class="file-loading">
                            <ion-spinner name="crescent"></ion-spinner>
                        </div>
                        <div v-else-if="resources.length === 0" class="file-empty">
                            No resources for this instance yet.
                        </div>
                        <template v-else>
                            <!-- Type filter pills -->
                            <div v-if="resourceTypeBuckets.size > 1" class="type-filter-bar">
                                <button
                                    type="button"
                                    class="type-pill"
                                    :class="{ 'type-pill--active': resourceTypeFilter === 'all' }"
                                    @click="resourceTypeFilter = 'all'">All</button>
                                <button
                                    v-for="bucket in resourceTypeBuckets"
                                    :key="bucket"
                                    type="button"
                                    class="type-pill"
                                    :class="{ 'type-pill--active': resourceTypeFilter === bucket }"
                                    @click="resourceTypeFilter = bucket">{{ BUCKET_LABELS[bucket] }}</button>
                            </div>
                            <div class="audio-list">
                                <div v-if="filteredResources.length === 0" class="file-empty">
                                    No resources match your search.
                                </div>
                                <div
                                    v-for="res of filteredResources"
                                    :key="res.id"
                                    class="audio-item">
                                    <ion-icon :icon="resourceIcon(res.mimeType)" class="resource-type-icon"></ion-icon>
                                    <div class="audio-item-info">
                                        <span class="audio-item-name">{{ res.resourceLabel || res.name }}</span>
                                        <span class="audio-item-meta">
                                            {{ res.name }}
                                            <template v-if="res.inUseCnt"> · Used in {{ res.inUseCnt }} journey{{ res.inUseCnt > 1 ? 's' : '' }}</template>
                                        </span>
                                    </div>
                                    <div class="audio-item-actions">
                                        <button
                                            type="button"
                                            class="action-btn edit-btn"
                                            title="Edit"
                                            @click="editResource(res)">
                                            <ion-icon :icon="createOutline"></ion-icon>
                                        </button>
                                        <button
                                            type="button"
                                            class="action-btn delete-btn"
                                            :class="{ 'action-btn--hidden': !!res.inUseCnt }"
                                            title="Delete"
                                            :disabled="deletingId === res.id || !!res.inUseCnt"
                                            @click="!res.inUseCnt && confirmDeleteResource(res)">
                                            <ion-spinner v-if="deletingId === res.id" name="crescent" class="delete-spinner"></ion-spinner>
                                            <ion-icon v-else :icon="trashOutline"></ion-icon>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </template>

                    <!-- Media group panel -->
                    <template v-else-if="selectedView === 'group' && selectedGroup">
                        <div class="file-panel-header">
                            <h2 class="group-title">{{ selectedGroup.name }}</h2>
                            <div class="panel-controls">
                                <input
                                    v-if="audioFiles.length > 0 || imageFiles.length > 0"
                                    v-model="searchQuery"
                                    type="search"
                                    class="search-input"
                                    placeholder="Search files…" />
                                <select v-if="audioFiles.length > 0" v-model="sortBy" class="sort-select">
                                    <option value="date-desc">Newest first</option>
                                    <option value="date-asc">Oldest first</option>
                                    <option value="name-asc">Name A–Z</option>
                                    <option value="name-desc">Name Z–A</option>
                                    <option value="size-desc">Largest first</option>
                                    <option value="size-asc">Smallest first</option>
                                </select>
                                <label class="upload-label header-upload-btn">
                                    <ion-icon :icon="cloudUploadOutline"></ion-icon>
                                    <span>Upload</span>
                                    <input
                                        type="file"
                                        :accept="uploadAccept"
                                        @change="handleUpload" />
                                </label>
                            </div>
                        </div>

                        <div v-if="loadingFiles" class="file-loading">
                            <ion-spinner name="crescent"></ion-spinner>
                        </div>

                        <div v-else-if="audioFiles.length === 0 && imageFiles.length === 0" class="file-empty">
                            No files in this group yet.
                        </div>

                        <div v-else class="file-panel-scroll">
                            <div
                                v-if="searchQuery.trim() && filteredFiles.length === 0 && filteredImageFiles.length === 0"
                                class="file-empty file-empty--inline">
                                No files match your search.
                            </div>

                            <div v-if="audioFiles.length > 0" class="file-list-section">
                                <div v-if="imageFiles.length > 0" class="file-section-label">Audio</div>
                                <template v-if="filteredFiles.length === 0">
                                    <div v-if="!searchQuery.trim()" class="file-empty file-empty--inline">No audio files.</div>
                                </template>
                                <div
                                    v-for="file of filteredFiles"
                                    :key="'a-' + file.id"
                                    class="audio-item">
                                    <div class="audio-item-info">
                                        <span class="audio-item-name">{{ file.name }}</span>
                                        <span class="audio-item-meta">
                                            {{ formatDate(file.createdDate) }}
                                            <template v-if="hasKnownMediaSizeKb(file.sizeKb)"> · {{ formatSizeKb(file.sizeKb) }}</template>
                                            <template v-if="file.inUseCnt"> · Used in {{ file.inUseCnt }} location{{ file.inUseCnt > 1 ? 's' : '' }}</template>
                                        </span>
                                        <span v-if="file.processingMsg" class="status status--error">
                                            {{ file.processingMsg }}
                                        </span>
                                        <span v-else-if="!hasKnownMediaSizeKb(file.sizeKb)" class="status status--pending">
                                            <ion-spinner name="crescent" class="processing-spinner"></ion-spinner> Processing…
                                        </span>
                                    </div>
                                    <div class="audio-item-actions">
                                        <button
                                            type="button"
                                            class="action-btn play-btn"
                                            :class="{ 'play-btn--playing': playingId === file.id, 'play-btn--hidden': !isLibraryAudioReady(file) }"
                                            :title="playingId === file.id ? 'Pause' : 'Play'"
                                            :disabled="!isLibraryAudioReady(file)"
                                            @click="isLibraryAudioReady(file) && togglePlay(file)">
                                            <ion-icon :icon="playingId === file.id ? pauseOutline : playOutline"></ion-icon>
                                        </button>
                                        <button
                                            type="button"
                                            class="action-btn delete-btn"
                                            :class="{ 'action-btn--hidden': !!file.inUseCnt }"
                                            title="Delete"
                                            :disabled="deletingId === file.id || !!file.inUseCnt"
                                            @click="!file.inUseCnt && confirmDelete(file)">
                                            <ion-spinner v-if="deletingId === file.id" name="crescent" class="delete-spinner"></ion-spinner>
                                            <ion-icon v-else :icon="trashOutline"></ion-icon>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="imageFiles.length > 0" class="file-list-section">
                                <div class="file-section-label">Icons</div>
                                <template v-if="filteredImageFiles.length === 0">
                                    <div v-if="searchQuery.trim()" class="file-empty file-empty--inline">No icons match your search.</div>
                                </template>
                                <div
                                    v-for="file of filteredImageFiles"
                                    :key="'i-' + file.id"
                                    class="audio-item">
                                    <div class="audio-item-info">
                                        <span class="audio-item-name">{{ file.filename }}</span>
                                        <span class="audio-item-meta">
                                            <template v-if="hasKnownMediaSizeKb(file.sizeKb)"> {{ formatSizeKb(file.sizeKb) }}</template>
                                            <template v-if="file.inUseCnt"> · Used in {{ file.inUseCnt }} location{{ file.inUseCnt > 1 ? 's' : '' }}</template>
                                        </span>
                                        <span v-if="file.processingMsg" class="status status--error">
                                            {{ file.processingMsg }}
                                        </span>
                                        <span v-else-if="!hasKnownMediaSizeKb(file.sizeKb)" class="status status--pending">
                                            <ion-spinner name="crescent" class="processing-spinner"></ion-spinner> Processing…
                                        </span>
                                    </div>
                                    <div class="audio-item-actions audio-item-actions--icons">
                                        <button
                                            type="button"
                                            class="action-btn preview-btn"
                                            title="Preview"
                                            :disabled="iconPreviewLoadingId === file.id"
                                            @click="openIconPreview(file)">
                                            <ion-spinner v-if="iconPreviewLoadingId === file.id" name="crescent" class="delete-spinner"></ion-spinner>
                                            <ion-icon v-else :icon="eyeOutline"></ion-icon>
                                        </button>
                                        <button
                                            v-if="!file.inUseCnt"
                                            type="button"
                                            class="action-btn delete-btn"
                                            title="Delete"
                                            :disabled="deletingId === file.id"
                                            @click="confirmDeleteImage(file)">
                                            <ion-spinner v-if="deletingId === file.id" name="crescent" class="delete-spinner"></ion-spinner>
                                            <ion-icon v-else :icon="trashOutline"></ion-icon>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </ion-content>

        <!-- Delete confirmation modal -->
        <div v-if="deleteTarget" class="confirm-overlay" @click.self="deleteTarget = null">
            <div class="confirm-modal">
                <h3 class="confirm-title">Delete "{{ deleteTarget.name }}"?</h3>
                <p v-if="inUseInfo && inUseInfo.inUse" class="confirm-warning">
                    This file is currently in use in
                    <strong v-if="inUseInfo.journeyCount > 0">{{ inUseInfo.journeyCount }} journey{{ inUseInfo.journeyCount > 1 ? 's' : '' }}</strong>
                    <template v-if="inUseInfo.journeyCount > 0 && inUseInfo.tabCount > 0"> and </template>
                    <strong v-if="inUseInfo.tabCount > 0">{{ inUseInfo.tabCount }} tab{{ inUseInfo.tabCount > 1 ? 's' : '' }}</strong>.
                    Deleting it will remove the audio from those locations.
                </p>
                <p v-else class="confirm-text">This action cannot be undone.</p>
                <div class="confirm-actions">
                    <button type="button" class="btn btn-secondary" @click="deleteTarget = null">Cancel</button>
                    <button type="button" class="btn btn-danger" :disabled="deletingId != null" @click="deleteFile">
                        Delete
                    </button>
                </div>
            </div>
        </div>
        <!-- Icon preview modal -->
        <div v-if="iconPreview" class="confirm-overlay" @click.self="closeIconPreview">
            <div class="icon-preview-modal" @click.stop>
                <h3 class="icon-preview-title">{{ iconPreview.filename }}</h3>
                <div class="icon-preview-frame">
                    <img v-if="iconPreview.objectUrl" :src="iconPreview.objectUrl" alt="" class="icon-preview-img" />
                </div>
                <div class="confirm-actions">
                    <button type="button" class="btn btn-secondary" @click="closeIconPreview">Close</button>
                </div>
            </div>
        </div>

        <!-- Image delete confirmation modal -->
        <div v-if="deleteImageTarget" class="confirm-overlay" @click.self="deleteImageTarget = null">
            <div class="confirm-modal">
                <h3 class="confirm-title">Delete "{{ deleteImageTarget.filename }}"?</h3>
                <p class="confirm-text">This action cannot be undone.</p>
                <div class="confirm-actions">
                    <button type="button" class="btn btn-secondary" @click="deleteImageTarget = null">Cancel</button>
                    <button type="button" class="btn btn-danger" :disabled="deletingId != null" @click="deleteImageFile">
                        Delete
                    </button>
                </div>
            </div>
        </div>
        <!-- Resource delete confirmation modal -->
        <div v-if="deleteTargetResource" class="confirm-overlay" @click.self="deleteTargetResource = null">
            <div class="confirm-modal">
                <h3 class="confirm-title">Delete "{{ deleteTargetResource.name }}"?</h3>
                <p v-if="deleteTargetResource.inUseCnt > 0" class="confirm-warning">
                    This resource is currently in use in
                    <strong>{{ deleteTargetResource.inUseCnt }} journey{{ deleteTargetResource.inUseCnt > 1 ? 's' : '' }}</strong>.
                    Deleting it will remove the resource from those locations.
                </p>
                <p v-else class="confirm-text">This action cannot be undone.</p>
                <div class="confirm-actions">
                    <button type="button" class="btn btn-secondary" @click="deleteTargetResource = null">Cancel</button>
                    <button type="button" class="btn btn-danger" :disabled="deletingId != null" @click="deleteResource">
                        Delete
                    </button>
                </div>
            </div>
        </div>

        <!-- Resource upload modal -->
        <div v-if="resourceUploadModal.open" class="confirm-overlay" @click.self="closeResourceUploadModal">
            <div class="confirm-modal resource-upload-modal">
                <!-- Step 1: file type or URL selection -->
                <template v-if="resourceUploadModal.step === 'select-type'">
                    <h3 class="confirm-title">Add Resource</h3>
                    <div class="resource-type-btns">
                        <label class="upload-label resource-type-btn">
                            <ion-icon :icon="cloudUploadOutline"></ion-icon>
                            <span>Upload file</span>
                            <input type="file" accept="audio/mpeg,audio/wav,audio/mp4,application/pdf" @change="onResourceFileSelected" />
                        </label>
                        <button type="button" class="upload-label resource-type-btn" @click="resourceUploadModal.step = 'url'">
                            <ion-icon :icon="globeOutline"></ion-icon>
                            <span>Add URL</span>
                        </button>
                    </div>
                    <div class="confirm-actions" style="margin-top:16px;">
                        <button type="button" class="btn btn-secondary" @click="closeResourceUploadModal">Cancel</button>
                    </div>
                </template>

                <!-- Step 2a: confirm file upload -->
                <template v-else-if="resourceUploadModal.step === 'confirm-file'">
                    <h3 class="confirm-title">{{ resourceUploadModal.name }}</h3>
                    <div class="resource-form">
                        <label class="resource-field-label">Display label <span class="resource-field-hint">(optional)</span></label>
                        <input
                            v-model="resourceUploadModal.label"
                            type="text"
                            class="search-input"
                            style="width:100%;"
                            :placeholder="resourceUploadModal.name.replace(/\.[^/.]+$/, '')" />
                        <label class="resource-field-label" style="margin-top:12px;">
                            <input type="checkbox" v-model="resourceUploadModal.shareable" style="margin-right:6px;" />
                            Allow sharing
                        </label>
                    </div>
                    <div class="confirm-actions">
                        <button type="button" class="btn btn-secondary" @click="closeResourceUploadModal">Cancel</button>
                        <button type="button" class="btn btn-primary" :disabled="resourceUploadModal.saving" @click="saveResourceUpload">
                            <ion-spinner v-if="resourceUploadModal.saving" name="crescent" class="delete-spinner"></ion-spinner>
                            <span v-else>Save</span>
                        </button>
                    </div>
                </template>

                <!-- Step 2b: add URL -->
                <template v-else-if="resourceUploadModal.step === 'url'">
                    <h3 class="confirm-title">Add URL</h3>
                    <div class="resource-form">
                        <label class="resource-field-label">URL</label>
                        <input
                            v-model="resourceUploadModal.url"
                            type="url"
                            class="search-input"
                            style="width:100%;"
                            placeholder="https://example.com" />
                        <label class="resource-field-label" style="margin-top:12px;">Display label <span class="resource-field-hint">(optional)</span></label>
                        <input
                            v-model="resourceUploadModal.label"
                            type="text"
                            class="search-input"
                            style="width:100%;"
                            placeholder="Link title…" />
                        <label class="resource-field-label" style="margin-top:12px;">
                            <input type="checkbox" v-model="resourceUploadModal.shareable" style="margin-right:6px;" />
                            Allow sharing
                        </label>
                    </div>
                    <div class="confirm-actions">
                        <button type="button" class="btn btn-secondary" @click="closeResourceUploadModal">Cancel</button>
                        <button type="button" class="btn btn-primary" :disabled="!resourceUploadModal.url || resourceUploadModal.saving" @click="saveResourceUpload">
                            <ion-spinner v-if="resourceUploadModal.saving" name="crescent" class="delete-spinner"></ion-spinner>
                            <span v-else>Save</span>
                        </button>
                    </div>
                </template>

                <!-- Step 3: edit existing resource -->
                <template v-else-if="resourceUploadModal.step === 'edit'">
                    <h3 class="confirm-title">Edit Resource</h3>
                    <div class="resource-form">
                        <label class="resource-field-label">Name</label>
                        <input
                            v-model="resourceUploadModal.name"
                            type="text"
                            class="search-input"
                            style="width:100%;"
                            placeholder="Resource name" />
                        <template v-if="resourceUploadModal.mimeType === 'text/x-uri'">
                            <label class="resource-field-label" style="margin-top:12px;">URL</label>
                            <input
                                v-model="resourceUploadModal.url"
                                type="url"
                                class="search-input"
                                style="width:100%;"
                                placeholder="https://example.com" />
                        </template>
                        <label class="resource-field-label" style="margin-top:12px;">Display label <span class="resource-field-hint">(optional)</span></label>
                        <input
                            v-model="resourceUploadModal.label"
                            type="text"
                            class="search-input"
                            style="width:100%;"
                            :placeholder="resourceUploadModal.name.replace(/\.[^/.]+$/, '')" />
                        <label class="resource-field-label" style="margin-top:12px;">
                            <input type="checkbox" v-model="resourceUploadModal.shareable" style="margin-right:6px;" />
                            Allow sharing
                        </label>
                    </div>
                    <div class="confirm-actions">
                        <button type="button" class="btn btn-secondary" @click="closeResourceUploadModal">Cancel</button>
                        <button type="button" class="btn btn-primary" :disabled="resourceUploadModal.saving" @click="saveResourceUpload">
                            <ion-spinner v-if="resourceUploadModal.saving" name="crescent" class="delete-spinner"></ion-spinner>
                            <span v-else>Save</span>
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </ion-page>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import {
    IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons,
    IonBackButton, IonIcon, IonSpinner,
    loadingController, alertController,
} from '@ionic/vue';
import { useStore } from 'vuex';
import {
    playOutline, pauseOutline, trashOutline, eyeOutline, cloudUploadOutline, globeOutline, createOutline,
    documentAttachOutline, musicalNotesOutline, imageOutline, videocamOutline, linkOutline,
} from 'ionicons/icons';
import { GET, DELETE, FILEUPLOAD, POST } from '@/services/HTTP';
import { formatSizeKb, hasKnownMediaSizeKb, isLibraryAudioReady } from '@/utils/mediaSizeDisplay';
import { waitUntilAudioReadyToPlay } from '@/utils/audioBlobPlayback';
import { uuid4 } from '@capacitor/core/dist/esm/util';

const AUDIO_GROUP_TYPE = 'cc61a205-70f7-4b8c-92a0-0a4ea2b93f22';
const IMAGE_GROUP_TYPE = 'cc61a205-70f7-4b8c-92a0-0a4ea2b93f21';

interface AudioFile {
    id: string;
    name: string;
    createdDate: string;
    sizeKb: number | null;
    processingMsg: string | null;
    instanceId: string | null;
    audioGroupId: string;
    inUseCnt: number;
}

interface ImageFile {
    id: string;
    filename: string;
    instanceId: string | null;
    sizeKb: number | null;
    processingMsg: string | null;
    inUseCnt: number;
}

interface AudioGroup {
    id: string;
    name: string;
    imageGroupTypeId: string;
    audioFiles: AudioFile[];
    images: ImageFile[];
}

interface InUseInfo {
    inUse: boolean;
    journeyCount: number;
    tabCount: number;
}

interface ResourceItem {
    id: string;
    name: string;
    mimeType: string;
    resourceLabel: string | null;
    url: string | null;
    inUseCnt: number;
    appInstanceId: string;
    shareable: boolean;
}

export default defineComponent({
    name: 'AudioLibraryManager',
    components: {
        IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons,
        IonBackButton, IonIcon, IonSpinner,
    },
    setup() {
        const store = useStore();
        const instanceId = computed(() => store.getters['app/selectedAppInstanceId']);

        const loadingGroups = ref(false);
        const loadingFiles = ref(false);
        const loadingResources = ref(false);
        const groups = ref<AudioGroup[]>([]);
        const selectedGroup = ref<AudioGroup | null>(null);
        const selectedView = ref<'group' | 'resources' | null>(null);
        const audioFiles = ref<AudioFile[]>([]);
        const imageFiles = ref<ImageFile[]>([]);
        const resources = ref<ResourceItem[]>([]);
        const deleteTargetResource = ref<ResourceItem | null>(null);
        const resourceUploadModal = ref({
            open: false,
            step: 'select-type' as 'select-type' | 'confirm-file' | 'url' | 'edit',
            name: '',
            label: '',
            url: '',
            shareable: false,
            saving: false,
            fileData: null as string | null,
            mimeType: '',
            editId: null as string | null,
        });
        const searchQuery = ref('');
        const sortBy = ref('date-desc');
        const resourceTypeFilter = ref<string>('all');
        const playingId = ref<string | null>(null);
        const currentAudio = ref<HTMLAudioElement | null>(null);
        const deletingId = ref<string | null>(null);
        const deleteTarget = ref<AudioFile | null>(null);
        const deleteImageTarget = ref<ImageFile | null>(null);
        const iconPreview = ref<{ filename: string; objectUrl: string } | null>(null);
        const iconPreviewLoadingId = ref<string | null>(null);
        const inUseInfo = ref<InUseInfo | null>(null);

        const groupFileCount = (group: AudioGroup) => {
            const aud = group.audioFiles?.length ?? 0;
            const imgs = group.images ?? [];
            const uniqueImages = new Set(imgs.map(i => i.id)).size;
            return aud + uniqueImages;
        };

        /** Dedupes by id (API can duplicate rows); search matches filename. */
        const filteredImageFiles = computed(() => {
            const q = searchQuery.value.trim().toLowerCase();
            const seen = new Set<string>();
            const unique = imageFiles.value.filter(f => {
                if (seen.has(f.id)) return false;
                seen.add(f.id);
                return true;
            });
            if (!q) return unique;
            return unique.filter(f => (f.filename ?? '').toLowerCase().includes(q));
        });

        const resourceIcon = (mimeType: string) => {
            if (!mimeType) return linkOutline;
            if (mimeType === 'text/x-uri' || mimeType === 'text/uri-list') return globeOutline;
            if (mimeType === 'application/pdf') return documentAttachOutline;
            if (mimeType.startsWith('audio/')) return musicalNotesOutline;
            if (mimeType.startsWith('image/')) return imageOutline;
            if (mimeType.startsWith('video/')) return videocamOutline;
            return linkOutline;
        };

        const uploadAccept = computed(() => {
            if (!selectedGroup.value) return '';
            const tid = selectedGroup.value.imageGroupTypeId?.toLowerCase();
            if (tid === IMAGE_GROUP_TYPE) return '.svg,image/svg+xml';
            return '.mp3,.wav,.m4a,.aac,.ogg,.flac';
        });

        const filteredFiles = computed(() => {
            let list = audioFiles.value;
            const q = searchQuery.value.trim().toLowerCase();
            if (q) list = list.filter(f => f.name.toLowerCase().includes(q));
            return [...list].sort((a, b) => {
                switch (sortBy.value) {
                    case 'date-asc':  return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
                    case 'name-asc':  return a.name.localeCompare(b.name);
                    case 'name-desc': return b.name.localeCompare(a.name);
                    case 'size-desc': return (b.sizeKb ?? -1) - (a.sizeKb ?? -1);
                    case 'size-asc':  return (a.sizeKb ?? Infinity) - (b.sizeKb ?? Infinity);
                    default:          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                }
            });
        });

        const resourceTypeBuckets = computed(() => {
            const buckets = new Set<string>();
            for (const r of resources.value) {
                const m = r.mimeType ?? '';
                if (m === 'text/x-uri' || m === 'text/uri-list') buckets.add('link');
                else if (m === 'application/pdf') buckets.add('pdf');
                else if (m.startsWith('audio/')) buckets.add('audio');
                else if (m.startsWith('image/')) buckets.add('image');
                else if (m.startsWith('video/')) buckets.add('video');
                else buckets.add('other');
            }
            return buckets;
        });

        const BUCKET_LABELS: Record<string, string> = {
            link: 'Links', pdf: 'PDF', audio: 'Audio',
            image: 'Images', video: 'Video', other: 'Other',
        };

        const mimeTypeBucket = (mimeType: string) => {
            const m = mimeType ?? '';
            if (m === 'text/x-uri' || m === 'text/uri-list') return 'link';
            if (m === 'application/pdf') return 'pdf';
            if (m.startsWith('audio/')) return 'audio';
            if (m.startsWith('image/')) return 'image';
            if (m.startsWith('video/')) return 'video';
            return 'other';
        };

        const filteredResources = computed(() => {
            let list = resources.value;
            if (resourceTypeFilter.value !== 'all') {
                list = list.filter(r => mimeTypeBucket(r.mimeType ?? '') === resourceTypeFilter.value);
            }
            const q = searchQuery.value.trim().toLowerCase();
            if (q) list = list.filter(r =>
                r.name.toLowerCase().includes(q) ||
                (r.resourceLabel ?? '').toLowerCase().includes(q)
            );
            return list;
        });

        const stopAudio = () => {
            if (currentAudio.value) {
                currentAudio.value.pause();
                if (currentAudio.value.src?.startsWith('blob:')) {
                    URL.revokeObjectURL(currentAudio.value.src);
                }
                currentAudio.value = null;
            }
            playingId.value = null;
        };

        const dedupeImagesById = (images: ImageFile[] | undefined): ImageFile[] => {
            if (!images?.length) return [];
            const seen = new Set<string>();
            return images.filter(img => {
                if (seen.has(img.id)) return false;
                seen.add(img.id);
                return true;
            });
        };

        const selectGroup = async (group: AudioGroup) => {
            selectedGroup.value = group;
            selectedView.value = 'group';
            searchQuery.value = '';
            stopAudio();
            audioFiles.value = group.audioFiles ?? [];
            imageFiles.value = dedupeImagesById(group.images);
        };

        const selectResources = () => {
            selectedGroup.value = null;
            selectedView.value = 'resources';
            searchQuery.value = '';
            resourceTypeFilter.value = 'all';
            stopAudio();
        };

        const loadingError = ref<string | null>(null);

        const loadGroups = async () => {
            if (!instanceId.value) {
                loadingGroups.value = false;
                loadingError.value = 'Select an app instance on the Home screen to see files for that app.';
                groups.value = [];
                resources.value = [];
                selectedGroup.value = null;
                selectedView.value = null;
                audioFiles.value = [];
                imageFiles.value = [];
                return;
            }
            loadingGroups.value = true;
            loadingError.value = null;
            try {
                const [groupsResult, resourcesResult] = await Promise.all([
                    GET(`/audio/groups/instance/${instanceId.value}`, false),
                    GET(`/instances/${instanceId.value}/resources`, false),
                ]);
                groups.value = groupsResult ?? [];
                resources.value = resourcesResult ?? [];
                // Auto-select the first group if there's only one and no resources
                if (groups.value.length === 1 && resources.value.length === 0) {
                    selectGroup(groups.value[0]);
                }
            } catch (e) {
                loadingError.value = 'Failed to load files.';
                console.error('[AudioLibraryManager] loadGroups error:', e);
            } finally {
                loadingGroups.value = false;
            }
        };

        const togglePlay = async (file: AudioFile) => {
            if (playingId.value === file.id) { stopAudio(); return; }
            stopAudio();
            playingId.value = file.id;
            try {
                const result = await GET(`/audio/${file.id}/stream`, true);
                if (playingId.value !== file.id) return;
                if (!result.blob.size) {
                    playingId.value = null;
                    const alert = await alertController.create({
                        header: 'Playback failed',
                        message: 'The audio file is empty or still processing.',
                        buttons: ['OK'],
                    });
                    await alert.present();
                    return;
                }
                const objectUrl = URL.createObjectURL(result.blob);
                const audio = new Audio();
                audio.preload = 'auto';
                audio.src = objectUrl;
                audio.onended = () => {
                    playingId.value = null;
                    currentAudio.value = null;
                    URL.revokeObjectURL(objectUrl);
                };
                currentAudio.value = audio;
                await waitUntilAudioReadyToPlay(audio);
                await audio.play();
            } catch {
                stopAudio();
                const alert = await alertController.create({
                    header: 'Playback failed',
                    message: 'Could not play this file. If it was just uploaded, wait for processing to finish.',
                    buttons: ['OK'],
                });
                await alert.present();
            }
        };

        const confirmDelete = async (file: AudioFile) => {
            inUseInfo.value = null;
            deleteTarget.value = file;
            try {
                inUseInfo.value = await GET(`/audio/${file.id}/in-use`, false);
            } catch {
                // proceed without in-use info
            }
        };

        const confirmDeleteImage = (file: ImageFile) => {
            deleteImageTarget.value = file;
        };

        const closeIconPreview = () => {
            if (iconPreview.value?.objectUrl) {
                URL.revokeObjectURL(iconPreview.value.objectUrl);
            }
            iconPreview.value = null;
        };

        const openIconPreview = async (file: ImageFile) => {
            closeIconPreview();
            iconPreviewLoadingId.value = file.id;
            try {
                const res = await GET(`/images/${file.id}/src`, true) as { blob: Blob };
                const objectUrl = URL.createObjectURL(res.blob);
                iconPreview.value = { filename: file.filename, objectUrl };
            } catch {
                const alert = await alertController.create({
                    header: 'Preview failed',
                    message: 'Could not load this icon from storage.',
                    buttons: ['OK'],
                });
                await alert.present();
            } finally {
                iconPreviewLoadingId.value = null;
            }
        };

        const deleteFile = async () => {
            if (!deleteTarget.value) return;
            const file = deleteTarget.value;
            deletingId.value = file.id;
            deleteTarget.value = null;
            try {
                await DELETE(`/audio/${file.id}`);
                audioFiles.value = audioFiles.value.filter(f => f.id !== file.id);
                if (selectedGroup.value) {
                    selectedGroup.value.audioFiles = selectedGroup.value.audioFiles.filter(f => f.id !== file.id);
                }                if (playingId.value === file.id) stopAudio();
            } catch (e) {
                const alert = await alertController.create({
                    header: 'Delete Failed',
                    message: typeof e === 'string' && e ? e : 'Unable to delete this file.',
                    buttons: ['OK'],
                });
                await alert.present();
            } finally {
                deletingId.value = null;
            }
        };

        const deleteImageFile = async () => {
            if (!deleteImageTarget.value) return;
            const file = deleteImageTarget.value;
            deletingId.value = file.id;
            deleteImageTarget.value = null;
            try {
                await DELETE(`/images/${file.id}`);
                imageFiles.value = imageFiles.value.filter(f => f.id !== file.id);
                if (selectedGroup.value) {
                    selectedGroup.value.images = selectedGroup.value.images.filter(f => f.id !== file.id);
                }
            } catch (e) {
                const alert = await alertController.create({
                    header: 'Delete Failed',
                    message: typeof e === 'string' && e ? e : 'Unable to delete this file.',
                    buttons: ['OK'],
                });
                await alert.present();
            } finally {
                deletingId.value = null;
            }
        };

        const confirmDeleteResource = (res: ResourceItem) => {
            deleteTargetResource.value = res;
        };

        const openResourceUploadModal = () => {
            resourceUploadModal.value = { open: true, step: 'select-type', name: '', label: '', url: '', shareable: false, saving: false, fileData: null, mimeType: '', editId: null };
        };

        const openResourceUrlModal = () => {
            resourceUploadModal.value = { open: true, step: 'url', name: '', label: '', url: '', shareable: false, saving: false, fileData: null, mimeType: 'text/x-uri', editId: null };
        };

        const editResource = (res: ResourceItem) => {
            resourceUploadModal.value = {
                open: true,
                step: 'edit',
                name: res.name,
                label: res.resourceLabel ?? '',
                url: res.url ?? '',
                shareable: res.shareable,
                saving: false,
                fileData: null,
                mimeType: res.mimeType,
                editId: res.id,
            };
        };

        const closeResourceUploadModal = () => {
            resourceUploadModal.value.open = false;
        };

        const onResourceFileSelected = (event: Event) => {
            const input = event.target as HTMLInputElement;
            if (!input.files?.length) return;
            const file = input.files[0];
            const MAX_SIZE = 7 * 1024 * 1024;
            if (file.size > MAX_SIZE) {
                alertController.create({ header: 'File too large', message: 'Maximum file size is 7 MB.', buttons: ['OK'] }).then(a => a.present());
                input.value = '';
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                resourceUploadModal.value.fileData = reader.result as string;
                resourceUploadModal.value.name = file.name;
                resourceUploadModal.value.mimeType = file.type;
                resourceUploadModal.value.step = 'confirm-file';
            };
            reader.readAsDataURL(file);
        };

        const saveResourceUpload = async () => {
            if (!instanceId.value) return;
            const m = resourceUploadModal.value;
            m.saving = true;
            try {
                const isEdit = m.step === 'edit';
                const isUrl = m.step === 'url' || (isEdit && m.mimeType === 'text/x-uri');
                if (isEdit && m.editId) {
                    await POST(`/resources/${m.editId}/update`, {
                        id: m.editId,
                        appInstanceId: instanceId.value,
                        name: m.name,
                        mimeType: m.mimeType,
                        url: isUrl ? (m.url.match(/^https?:\/\//i) ? m.url : `https://${m.url}`) : null,
                        resourceLabel: m.label || null,
                        shareable: m.shareable,
                    });
                } else {
                    const body: Record<string, unknown> = {
                        id: uuid4(),
                        appInstanceId: instanceId.value,
                        name: isUrl ? (m.label || m.url) : m.name,
                        mimeType: isUrl ? 'text/x-uri' : m.mimeType,
                        resourceData: isUrl ? '' : (m.fileData ? m.fileData.split(',')[1] : ''),
                        url: isUrl ? (m.url.match(/^https?:\/\//i) ? m.url : `https://${m.url}`) : null,
                        resourceLabel: m.label || null,
                        shareable: m.shareable,
                    };
                    await POST(`/instances/${instanceId.value}/resources`, body);
                }
                const updated = await GET(`/instances/${instanceId.value}/resources`, false);
                resources.value = updated ?? [];
                closeResourceUploadModal();
            } catch (e) {
                const alert = await alertController.create({ header: 'Save Failed', message: 'Could not save the resource.', buttons: ['OK'] });
                await alert.present();
            } finally {
                m.saving = false;
            }
        };

        const deleteResource = async () => {
            if (!deleteTargetResource.value) return;
            const res = deleteTargetResource.value;
            deletingId.value = res.id;
            deleteTargetResource.value = null;
            try {
                await DELETE(`/instances/${instanceId.value}/resources/${res.id}`);
                resources.value = resources.value.filter(r => r.id !== res.id);
            } catch (e) {
                const alert = await alertController.create({
                    header: 'Delete Failed',
                    message: 'This resource may be in use and cannot be deleted.',
                    buttons: ['OK'],
                });
                await alert.present();
            } finally {
                deletingId.value = null;
            }
        };

        // Poll a newly-uploaded audio file until sizeKb is populated, then update in-place.
        const pollProcessing = (fileId: string) => {
            const INTERVAL = 3000;
            const MAX_ATTEMPTS = 40; // 2 min max
            let attempts = 0;
            const timer = setInterval(async () => {
                attempts++;
                try {
                    const updated: AudioFile = await GET(`/audio/${fileId}`, false);
                    if (updated && (hasKnownMediaSizeKb(updated.sizeKb) || updated.processingMsg)) {
                        clearInterval(timer);
                        const idx = audioFiles.value.findIndex(f => f.id === fileId);
                        if (idx !== -1) {
                            audioFiles.value[idx] = { ...audioFiles.value[idx], sizeKb: updated.sizeKb, processingMsg: updated.processingMsg };
                        }
                        if (selectedGroup.value) {
                            const gi = selectedGroup.value.audioFiles.findIndex(f => f.id === fileId);
                            if (gi !== -1) selectedGroup.value.audioFiles[gi] = { ...selectedGroup.value.audioFiles[gi], sizeKb: updated.sizeKb, processingMsg: updated.processingMsg };
                        }
                    }
                } catch { /* ignore */ }
                if (attempts >= MAX_ATTEMPTS) clearInterval(timer);
            }, INTERVAL);
        };

        const handleUpload = async (event: Event) => {
            if (!selectedGroup.value) return;
            const input = event.target as HTMLInputElement;
            if (!input.files?.length) return;
            const file = input.files[0];
            const isImageGroup = selectedGroup.value.imageGroupTypeId?.toLowerCase() === IMAGE_GROUP_TYPE;

            const loader = await loadingController.create({ message: 'Uploading…' });
            await loader.present();
            try {
                if (isImageGroup) {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('imageGroupId', selectedGroup.value.id);
                    if (instanceId.value) formData.append('instanceId', instanceId.value);

                    const created = await FILEUPLOAD('/images/upload', formData) as ImageFile & { sizeKb?: number | null };
                    if (created?.id) {
                        const row: ImageFile = {
                            id: created.id,
                            filename: created.filename,
                            instanceId: created.instanceId ?? null,
                            sizeKb: created.sizeKb ?? null,
                            processingMsg: null,
                            inUseCnt: created.inUseCnt ?? 0,
                        };
                        imageFiles.value.unshift(row);
                        selectedGroup.value.images = [row, ...selectedGroup.value.images];
                    }
                } else {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('audioGroupId', selectedGroup.value.id);
                    if (instanceId.value) formData.append('instanceId', instanceId.value);

                    const created: AudioFile = await FILEUPLOAD('/audio/upload', formData);
                    if (created) {
                        audioFiles.value.unshift(created);
                        selectedGroup.value.audioFiles = [created, ...selectedGroup.value.audioFiles];
                        if (!hasKnownMediaSizeKb(created.sizeKb)) pollProcessing(created.id);
                    }
                }
            } finally {
                await loader.dismiss();
                input.value = '';
            }
        };

        const handleResourceUpload = async (event: Event) => { void event; }; // replaced by modal

        const formatDate = (iso: string) => {
            if (!iso) return '';
            return new Date(iso).toLocaleDateString();
        };

        watch(instanceId, () => { groups.value = []; resources.value = []; selectedGroup.value = null; selectedView.value = null; audioFiles.value = []; imageFiles.value = []; loadGroups(); }, { immediate: true });
        onMounted(() => store.dispatch('app/setPageTitle', 'My Files'));
        onUnmounted(() => {
            stopAudio();
            closeIconPreview();
        });

        return {
            loadingGroups, loadingFiles, loadingResources, loadingError,
            groups, selectedGroup, selectedView, audioFiles, imageFiles,
            resources, filteredFiles, filteredImageFiles, filteredResources, searchQuery, sortBy,
            resourceTypeFilter, resourceTypeBuckets, BUCKET_LABELS,
            playingId, deletingId, deleteTarget, deleteImageTarget, deleteTargetResource, inUseInfo,
            resourceUploadModal,
            groupFileCount, uploadAccept, resourceIcon,
            selectGroup, selectResources, togglePlay, confirmDelete, deleteFile,
            confirmDeleteImage, deleteImageFile, iconPreview, iconPreviewLoadingId,
            openIconPreview, closeIconPreview,
            confirmDeleteResource, deleteResource, handleUpload, handleResourceUpload,
            openResourceUploadModal, openResourceUrlModal, closeResourceUploadModal, editResource,
            onResourceFileSelected, saveResourceUpload,
            formatDate, formatSizeKb, hasKnownMediaSizeKb, isLibraryAudioReady,
            playOutline, pauseOutline, trashOutline, eyeOutline, cloudUploadOutline, globeOutline, createOutline,
            documentAttachOutline, musicalNotesOutline, imageOutline, videocamOutline, linkOutline,
        };
    },
});
</script>

<style scoped>
.manager-layout {
    display: flex;
    height: 100%;
    min-height: calc(100vh - 8px);
}

/* --- Sidebar --- */
.group-sidebar {
    width: 220px;
    flex-shrink: 0;
    border-right: 1px solid var(--ion-color-light-shade, #d7d8da);
    display: flex;
    flex-direction: column;
    background: var(--ion-item-background, #fff);
}

.sidebar-loading {
    display: flex;
    justify-content: center;
    padding: 24px;
}

.sidebar-error {
    padding: 12px 16px;
    font-size: 0.85em;
    color: var(--ion-color-danger, #eb445a);
}

.group-list {
    flex: 1;
    overflow-y: auto;
    padding: 72px 0 8px;
}

.group-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 10px 16px;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    font-size: 0.95em;
    color: var(--ion-text-color);
    transition: background 0.12s;
    gap: 8px;
}

.group-item:hover {
    background: var(--ion-color-light, #f4f5f8);
}

.group-item--active {
    background: var(--ion-color-light-shade, #e2e3e8);
    font-weight: 600;
}

.group-item--active:hover {
    background: var(--ion-color-light-shade, #d8d9de);
}

.group-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.group-count-pill {
    flex-shrink: 0;
    padding: 2px 9px;
    border-radius: 20px;
    border: 1px solid var(--ion-color-light-shade, #d7d8da);
    font-size: 0.75em;
    color: var(--ion-color-medium, #92949c);
    line-height: 1.4;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.group-item--active .group-count-pill {
    background: var(--ion-color-primary, #3880ff);
    border-color: var(--ion-color-primary, #3880ff);
    color: #fff;
}

/* --- File panel --- */
.file-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 20px 24px;
    gap: 16px;
}

.file-panel-empty {
    margin: auto;
    color: var(--ion-color-medium);
    font-size: 0.95em;
}

.file-panel-header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.group-title {
    margin: 0;
    font-size: 1.2em;
    font-weight: 600;
    flex: 1;
}

.panel-controls {
    display: flex;
    gap: 8px;
}

.search-input {
    padding: 7px 12px;
    border: 1px solid var(--ion-color-light-shade, #d7d8da);
    border-radius: 6px;
    font-size: 0.9em;
    background: var(--ion-item-background, #fff);
    color: var(--ion-text-color, #000);
    outline: none;
    width: 220px;
}

.search-input:focus {
    border-color: var(--ion-color-primary, #3880ff);
}

.sort-select {
    padding: 7px 10px;
    border: 1px solid var(--ion-color-light-shade, #d7d8da);
    border-radius: 6px;
    font-size: 0.85em;
    background: var(--ion-item-background, #fff);
    color: var(--ion-text-color, #000);
    cursor: pointer;
    outline: none;
}

.sort-select:focus {
    border-color: var(--ion-color-primary, #3880ff);
}

/* --- Type filter pills --- */
.type-filter-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 16px 4px;
}

.type-pill {
    padding: 3px 12px;
    border-radius: 20px;
    border: 1px solid var(--ion-color-light-shade, #d7d8da);
    background: transparent;
    font-size: 0.82em;
    color: var(--ion-color-medium, #92949c);
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.type-pill:hover {
    border-color: var(--ion-color-primary, #3880ff);
    color: var(--ion-color-primary, #3880ff);
}

.type-pill--active {
    background: var(--ion-color-primary, #3880ff);
    border-color: var(--ion-color-primary, #3880ff);
    color: #fff;
}

.file-loading,
.file-empty {
    text-align: center;
    padding: 32px;
    color: var(--ion-color-medium);
}

/* Single scroll area for mixed audio + images (avoids two flex:1 columns fighting for height). */
.file-panel-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    -webkit-overflow-scrolling: touch;
}

.file-list-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.file-empty--inline {
    padding: 12px 8px;
    text-align: left;
}

.audio-list {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.audio-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-radius: 8px;
    background: var(--ion-color-light, #f4f5f8);
    transition: background 0.12s;
}

.audio-item:hover {
    background: var(--ion-color-light-shade, #e2e3e8);
}

.audio-item-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
}

.audio-item-name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.audio-item-meta {
    font-size: 0.8em;
    color: var(--ion-color-medium);
}

.status {
    font-size: 0.8em;
}

.status--pending {
    color: var(--ion-color-warning, #ffc409);
}

.status--error {
    color: var(--ion-color-danger, #eb445a);
}

.audio-item-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 12px;
    flex-shrink: 0;
    /* always reserve space for play + delete (34px each + 8px gap) */
    width: 76px;
    justify-content: flex-end;
}

.action-btn {
    border: none;
    border-radius: 50%;
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 1em;
    transition: background 0.12s;
}

.action-btn:disabled {
    opacity: 0.5;
    cursor: default;
}

.play-btn {
    background: var(--ion-color-primary, #3880ff);
    color: #fff;
}

.play-btn:hover:not(:disabled) {
    background: var(--ion-color-primary-shade, #3171e0);
}

.play-btn--playing {
    background: var(--ion-color-danger, #eb445a);
}

.play-btn--playing:hover {
    background: var(--ion-color-danger-shade, #cf3c4f);
}

.delete-btn {
    background: var(--ion-color-light-shade, #e2e3e8);
    color: var(--ion-color-danger, #eb445a);
}

.delete-btn:hover:not(:disabled) {
    background: var(--ion-color-danger, #eb445a);
    color: #fff;
}

.audio-item-actions--icons {
    width: auto;
    min-width: 78px;
    justify-content: flex-end;
}

.preview-btn {
    background: var(--ion-color-light-shade, #e2e3e8);
    color: var(--ion-color-primary, #3880ff);
}

.preview-btn:hover:not(:disabled) {
    background: var(--ion-color-primary, #3880ff);
    color: #fff;
}

.icon-preview-modal {
    background: var(--ion-item-background, #fff);
    border-radius: 12px;
    padding: 20px 24px;
    max-width: 420px;
    width: 90vw;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

.icon-preview-title {
    margin: 0 0 12px;
    font-size: 1.05em;
    font-weight: 600;
    word-break: break-word;
}

.icon-preview-frame {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 140px;
    background: var(--ion-color-light, #f4f5f8);
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 8px;
}

.icon-preview-img {
    max-width: 100%;
    max-height: 280px;
    object-fit: contain;
}

.delete-spinner {
    width: 16px;
    height: 16px;
}

.resource-type-icon {
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-right: 10px;
    color: var(--ion-color-medium);
}

.file-section-label {
    font-size: 0.75em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ion-color-medium);
    padding: 4px 2px 6px;
}

/* --- Upload --- */

.upload-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px dashed var(--ion-color-medium, #92949c);
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.88em;
    color: var(--ion-color-medium, #92949c);
    transition: border-color 0.15s, color 0.15s;
}

.upload-label:hover {
    border-color: var(--ion-color-primary, #3880ff);
    color: var(--ion-color-primary, #3880ff);
}

.upload-label input[type="file"] {
    display: none;
}

.header-upload-btn {
    flex-shrink: 0;
    margin-left: 8px;
}

.play-btn--hidden,
.action-btn--hidden {
    visibility: hidden;
    pointer-events: none;
}

.processing-spinner {
    width: 12px;
    height: 12px;
    vertical-align: middle;
    margin-right: 4px;
}

.edit-btn {
    background: var(--ion-color-light-shade, #e2e3e8);
    color: var(--ion-color-primary, #3880ff);
}

.edit-btn:hover:not(:disabled) {
    background: var(--ion-color-primary, #3880ff);
    color: #fff;
}

/* --- Resource upload modal --- */
.resource-upload-modal {
    width: min(480px, 94vw);
}

.resource-type-btns {
    display: flex;
    gap: 12px;
    margin-top: 12px;
    flex-wrap: wrap;
}

.resource-type-btn {
    flex: 1;
    justify-content: center;
    padding: 14px 16px;
    font-size: 0.95em;
}

.resource-form {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 12px 0 20px;
}

.resource-field-label {
    font-size: 0.85em;
    font-weight: 500;
    color: var(--ion-text-color);
    margin-top: 4px;
    display: flex;
    align-items: center;
}

.resource-field-hint {
    font-weight: 400;
    color: var(--ion-color-medium);
    margin-left: 4px;
}

.btn-primary {
    background: var(--ion-color-primary, #3880ff);
    color: #fff;
    min-width: 64px;
}

.btn-primary:hover:not(:disabled) {
    background: var(--ion-color-primary-shade, #3171e0);
}

.btn-primary:disabled {
    opacity: 0.5;
    cursor: default;
}

/* --- Delete confirmation --- */
.confirm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.confirm-modal {
    background: var(--ion-background-color, #fff);
    border-radius: 12px;
    padding: 24px;
    width: min(440px, 92vw);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
}

.confirm-title {
    margin: 0 0 12px;
    font-size: 1.05em;
    font-weight: 600;
    word-break: break-word;
}

.confirm-text {
    margin: 0 0 20px;
    color: var(--ion-color-medium);
    font-size: 0.9em;
}

.confirm-warning {
    margin: 0 0 20px;
    color: var(--ion-color-danger, #eb445a);
    font-size: 0.9em;
    line-height: 1.5;
}

.confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

#main-content > .ion-page > ion-content::part(scroll) {
    margin-top: 0px;
}

.btn {
    padding: 8px 20px;
    border: none;
    border-radius: 6px;
    font-size: 0.9em;
    cursor: pointer;
    transition: background 0.12s;
}

.btn:disabled {
    opacity: 0.5;
    cursor: default;
}

.btn-secondary {
    background: var(--ion-color-light, #f4f5f8);
    color: var(--ion-text-color);
}

.btn-secondary:hover:not(:disabled) {
    background: var(--ion-color-light-shade, #e2e3e8);
}

.btn-danger {
    background: var(--ion-color-danger, #eb445a);
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: var(--ion-color-danger-shade, #cf3c4f);
}
</style>
